import { useUser } from "@clerk/clerk-react";
import type { ReactNode } from "react";
import { clerkEnabled } from "@/services/clerk";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * An email address to publish or be reached at: the verified addresses on the
 * member's account, and nothing else.
 *
 * It used to offer "Another address…" with a free box, so a shared info@ could
 * be published without becoming a sign-in address. Adding an address is the
 * Account page's job now, and it verifies what it adds, which is the better
 * trade: every address the directory publishes is one somebody has confirmed
 * they can read, and nobody publishes a typo.
 *
 * An address already on the profile that is not on the account stays in the
 * list, because 447 members were imported with addresses Clerk has never seen
 * and dropping one on the next save would be losing their data quietly.
 *
 * Where the addresses come from is said once, by whoever renders this: the
 * profile page points at the Account page, and the registration form has the
 * account's own card directly above.
 *
 * Without Clerk configured it is a plain input, which is also what the tests
 * that run without Clerk see.
 */
type Props = {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
};

export function EmailChoice(props: Props) {
  return clerkEnabled ? <WithAccount {...props} /> : <Plain {...props} />;
}

function Plain({ id, label, value, onChange, hint }: Props) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="email" value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

/** Split out so the Clerk hook only runs under a ClerkProvider. */
function WithAccount({ id, label, value, onChange, hint }: Props) {
  const { user } = useUser();
  const verified = (user?.emailAddresses ?? [])
    .filter((a) => a.verification.status === "verified")
    .map((a) => a.emailAddress);
  const options = verified.includes(value) || !value ? verified : [value, ...verified];

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Choose an address" />
        </SelectTrigger>
        <SelectContent>
          {options.map((address) => (
            <SelectItem key={address} value={address}>
              {address}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}
